import { useState } from 'react'
import {
  accountCodeError,
  normalizeAccountCode,
  type BudgetCode,
  type NamedCodeLike,
  type NewExternalBudgetCode,
} from '@budgetmonkey/shared'
import { Button, ErrorNote, Field, Input } from '@/components/ui'
import { AliasEditor } from './AliasEditor'

/**
 * Add or edit an external (colleague's) budget code — workflow 7.7.
 *
 * Deliberately minimal: this gets filled in mid-task, while filing someone
 * else's receipt, so only the code and the owner's name are required. Short
 * label defaults to the owner name and aliases can be skipped entirely.
 *
 * External codes appear on reconciliation packets but take no part in any
 * budget math — no beginning balance, no threshold, nothing to fill in here
 * that the dashboard would ever read. That difference is why this exists at all
 * rather than editing an external code through `BudgetCodeForm`, which would
 * ask for a fund, a category and a beginning balance that mean nothing here.
 *
 * `initial` prefills every field the form owns. A create-only form pressed into
 * editing writes a blank over whatever it failed to prefill, which is how the
 * transaction edit form lost fields that weren't on it.
 */
export function ExternalCodeForm({
  initial,
  currentCodeId,
  existingCodes,
  submitLabel = 'Add external code',
  onSubmit,
  onCancel,
  pending,
  error,
}: {
  initial?: Partial<BudgetCode> | undefined
  currentCodeId?: string | undefined
  existingCodes: readonly NamedCodeLike[]
  submitLabel?: string
  onSubmit: (values: NewExternalBudgetCode) => void
  onCancel: () => void
  pending: boolean
  error?: string | undefined
}) {
  const [code, setCode] = useState(initial?.code ?? '')
  const [ownerName, setOwnerName] = useState(initial?.externalOwnerName ?? '')
  // A short label equal to the owner name is one nobody typed — it's the
  // default this form applies when the field is left empty. Prefilling it would
  // pin the old spelling: rename the owner and the packet keeps printing the
  // name before the rename, from a box that says it defaults to the owner's
  // name. Left empty, the placeholder tracks what's being typed and the
  // default is applied again on save. A label that differs was chosen on
  // purpose, so it is prefilled and kept.
  const [shortLabel, setShortLabel] = useState(
    initial?.shortLabel === initial?.externalOwnerName ? '' : (initial?.shortLabel ?? ''),
  )
  const [aliases, setAliases] = useState<string[]>([...(initial?.aliases ?? [])])
  const [touched, setTouched] = useState(false)

  const codeProblem = touched ? accountCodeError(code) : null
  const normalizedCode = normalizeAccountCode(code)
  const ownerProblem =
    touched && ownerName.trim() === ''
      ? "Required — it's how these charges are grouped."
      : null

  const canSubmit = normalizedCode !== null && ownerName.trim() !== ''

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    setTouched(true)
    if (!canSubmit || normalizedCode === null) return
    onSubmit({
      code: normalizedCode,
      externalOwnerName: ownerName.trim(),
      ...(shortLabel.trim() !== '' ? { shortLabel: shortLabel.trim() } : {}),
      aliases,
    })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5" noValidate>
      {error !== undefined && <ErrorNote>{error}</ErrorNote>}

      <p className="text-sm text-slate-600">
        A colleague's budget code you're charging on their behalf. It'll appear on
        your reconciliation packet but won't count against any of your balances.
      </p>

      <Field
        label="Account code"
        required
        error={codeProblem ?? undefined}
        hint="Theirs, not yours. Nine segments."
      >
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onBlur={() => setTouched(true)}
          placeholder="10-1100-610-000-00-000-000-100-0000"
          invalid={codeProblem !== null}
          className="font-mono text-sm"
          inputMode="numeric"
          autoComplete="off"
        />
      </Field>

      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Owner name" required error={ownerProblem ?? undefined}>
          <Input
            value={ownerName}
            onChange={(e) => setOwnerName(e.target.value)}
            onBlur={() => setTouched(true)}
            placeholder="Ms. Smith"
            invalid={ownerProblem !== null}
          />
        </Field>

        <Field label="Short label" hint="Defaults to the owner's name.">
          <Input
            value={shortLabel}
            onChange={(e) => setShortLabel(e.target.value)}
            placeholder={ownerName.trim() || 'Ms. Smith'}
            maxLength={24}
          />
        </Field>
      </div>

      <AliasEditor
        code={normalizedCode ?? code}
        aliases={aliases}
        onChange={setAliases}
        existingCodes={existingCodes}
        currentCodeId={currentCodeId}
      />

      <div className="flex gap-3 pt-1">
        <Button type="submit" variant="primary" disabled={pending}>
          {pending ? 'Saving…' : submitLabel}
        </Button>
        <Button type="button" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
      </div>
    </form>
  )
}
